import { Box, Dialog, DialogContent, Grid } from "@mui/material";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { VerificationAgencyClientService } from "../../../Component/Api/VerificationAgency/VerificationAgencyClient/VerificationAgencyClientService";
import { ConfirmationDialog } from "../../../Component/Common/Components/ConfirmationDialog";
import { DataTable } from "../../../Component/Common/Components/DataTable/DataTable";
import { MDBox } from "../../../Component/Common/Components/MDBox";
import { ColumnSortOption } from "../../../Component/Model/Dto/ColumnSortOption";
import { BEEGeneratedCertificate } from "../../../Component/Model/Dto/VerificationAgency/VerificationAgencyClient";
import { EntriesPerPage } from "../../../Component/Model/Table/Table";
import { urltoFile } from "../../../Component/Model/helpers";
import { CustomDialogTitle } from "../../Common/Custom/CustomDialogTitle";
import { VAClientCertificateTableMapper } from "./CertificatesTableColumn";

interface IProps {
    showModal: boolean;
    scorecardId: string,
    handleCancel: () => void;
}

export const VAClientCertificateModal = (props: IProps) => {

    const [certificateData, setCertificateData] = useState<BEEGeneratedCertificate.Dto[]>([]);
    const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [entriesPerPage, setEntriesPerPage] = useState<EntriesPerPage>({ defaultValue: 10 });
    const [filteredItemsCount, setFilteredItemsCount] = useState<number>(1);
    const [totalItemsCount, setTotalItemsCount] = useState<number>(1);
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [selectedCertificateId, setSelectedCertificateId] = useState<string>("");
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [columnSortOptions, setColumnSortOptions] = useState<ColumnSortOption[]>([]);

    const entriesPerPageChanged = (entriesPerPage: number) => {
        setEntriesPerPage({ defaultValue: entriesPerPage });
        setPageNumber(1);
    }

    const pageNumberChanged = (pageNumber: number) => {
        setPageNumber(pageNumber);
    }

    const columnSortingChanged = (sortOption: ColumnSortOption) => {
        setColumnSortOptions([sortOption]);
        setPageNumber(1);
    }

    const handleDownloadCertificate = async (id: string) => {
        try {
            const result = await VerificationAgencyClientService.DownloadCertificate(id);
            const filename = result.data.fileDownloadName ?? "";
            const file = await urltoFile('data:text/plain;base64,' + result.data.fileContents, filename, 'application/pdf')
            const url = URL.createObjectURL(file);
            const documentLink = document.createElement("a");
            documentLink.setAttribute("href", url);
            documentLink.setAttribute("download", result.data.fileDownloadName !== undefined ? result.data.fileDownloadName : filename);
            document.body.appendChild(documentLink);
            documentLink.click();
            document.body.removeChild(documentLink);
        } catch (error) {

        }
    }

    const handleDeleteCertificate = (id: string) => {
        setShowConfirmationDialog(true);
        setSelectedCertificateId(id);
    }


    const handleCloseConfirmation = () => {
        setShowConfirmationDialog(false);
        setSelectedCertificateId("");
    }

    const certificatesSearchRequest: BEEGeneratedCertificate.SearchRequest = {
        pageNumber: pageNumber,
        pageSize: entriesPerPage.defaultValue!,
        columnSortOptions: columnSortOptions,
        scorecardId: props.scorecardId
    };

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const certificatesResult = await VerificationAgencyClientService.SearchGeneratedCertificates(certificatesSearchRequest);
            setCertificateData(certificatesResult.data.results);
            setFilteredItemsCount(certificatesResult.data.filteredItemsCount);
            setTotalItemsCount(certificatesResult.data.totalItemsCount);

            if (certificatesResult.data.filteredItemsCount <= 0) {
                toast.warning("This scorecard does not have any generated certificates");
            }

        } catch (error) {
            setCertificateData([]);
            setFilteredItemsCount(0);
            setTotalItemsCount(0);
        } finally {
            setIsLoading(false);
        }
    }

    const onDelete = async () => {
        setIsDeleting(true);
        try {
            await VerificationAgencyClientService.DeleteGeneratedCertificate(selectedCertificateId);
            fetchData();
        } catch (error) {

        } finally {
            setIsDeleting(false);
            handleCloseConfirmation();
            toast.success("Certificate deleted successfully");
        }
    }

    useEffect(() => {
        fetchData();
    }, [entriesPerPage, pageNumber, columnSortOptions]); // eslint-disable-line react-hooks/exhaustive-deps

    const tableData = VAClientCertificateTableMapper(certificateData, handleDeleteCertificate, handleDownloadCertificate); //Map the API data into the table

    return (
        <Box >
            <Dialog open={props.showModal} onClose={props.handleCancel} aria-labelledby="form-dialog-title" maxWidth='xl' fullWidth>
                <CustomDialogTitle id="customized-dialog-title" onClose={props.handleCancel}>
                    View Certificates
                </CustomDialogTitle>
                <DialogContent>
                    <Grid container justifyContent='center'>
                        <Grid item xs={11.5} lg={111.5}>
                            <MDBox>
                                <DataTable
                                    key={totalItemsCount}
                                    table={tableData!}
                                    entriesPerPage={entriesPerPage}
                                    pageNumber={pageNumber}
                                    filteredItemsCount={filteredItemsCount}
                                    totalItemsCount={totalItemsCount}
                                    entriesPerPageChanged={entriesPerPageChanged}
                                    pageNumberChanged={pageNumberChanged}
                                    isLoading={isLoading}
                                    columnSortingChanged={columnSortingChanged}
                                    manualSorting={true}
                                ></DataTable>

                                <ConfirmationDialog
                                    showDialog={showConfirmationDialog}
                                    handleAccept={onDelete}
                                    handleCancel={handleCloseConfirmation}
                                    heading={"Delete Entry"}
                                    message={"Are you sure you want to delete this entry?"}
                                    isLoading={isDeleting}
                                />
                            </MDBox>
                        </Grid>
                    </Grid>
                </DialogContent>
            </Dialog>
        </Box>
    )
}